import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CHANGE_NONE,
  INPUT_TYPE_LABEL,
  ORIGINS,
  ORIGIN_LABEL,
  daysInLab,
  itemsText,
  kitStatus,
  listsAsOverdue,
  matchesSearch,
  type InputType,
  type Kit,
} from '@/domain';
import { useSession } from '@/application/usecases/useSession';
import { useTableOrder } from '@/application/usecases/useTableOrder';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import { formatDuration } from '@/application/timer/timerStore';
import { strings } from '@/shared/strings';
import { formatDay, labToday } from '@/shared/format/instant';
import { CaseTable } from '@/ui/data/CaseTable';
import type { PaginationProps } from '@/ui/data/Pagination';
import { DeliveryDate } from '@/ui/data/DeliveryDate';
import { CasePhotoCount } from '@/features/queue/CasePhotos';
import { ORIGIN_SORT_KEYS, casePresets } from '@/ui/data/casePresets';
import { FilterBar, type FilterDef } from '@/ui/data/FilterBar';
import { SortSelect } from '@/ui/data/SortSelect';
import type { Column } from '@/ui/data/ResponsiveTable';
import { StatusWithNote } from '@/ui/data/StatusDot';
import { Button } from '@/ui/primitives/Button';
import { AssignDialog } from '@/features/queue/AssignDialog';
import { SelectionActions } from '@/features/bulk/SelectionActions';
import {
  commonStageOf,
  usePermittedActions,
  type SelectionAction,
} from '@/features/bulk/permittedActions';
import { useSelection } from '@/ui/data/useSelection';

/**
 * A figure over the table pressed: what the table shows next. Every case, the
 * overdue, the reworked, or every case with the longest waiting first. `at`
 * tells a second press of the same figure from the first. The lab's request of
 * 17 October 2026, item 1.
 */
export interface StageFocus {
  show: 'all' | 'overdue' | 'rework' | 'oldest';
  at: number;
}

const ANYONE = 'anyone';
const UNASSIGNED = 'unassigned';
/** Prefixed, so no user id can ever read as one of the two choices above. */
const person = (userId: string) => `user:${userId}`;

export interface StageKitTableProps {
  kits: Kit[];
  caption: string;
  emptyHeading: string;
  emptyDetail: string;
  onRowClick: (kit: Kit) => void;
  /**
   * Which table this is, for the order the reader last chose. The stage detail
   * and Final Check share this component and are two different lists to work
   * from, so they remember separately.
   */
  tableKey: string;
  /**
   * The key the reader's layout of this table is kept under, where it is not
   * `tableKey`: every bench of the production line is one table to lay out,
   * as it is one size of page. The request of 7 October 2026, section 3.
   */
  layoutKey?: string;
  /**
   * The page under the rows, or nothing. Section 10.5 of the request of
   * 16 September 2026: the stage detail and Final Check take one, and a
   * technician's queue does not.
   */
  paging?: PaginationProps;
  /** Called when a filter or the order changes, so the reader goes back to page one. */
  onListChanged?: () => void;
  /**
   * The order the SERVER should sort the list by, when the reader chooses one
   * of the origin sorts, and undefined for every other order. Section 2 of the
   * request of 24 September 2026: the list is paged, so an origin sorted here
   * would come first on page one and again on page two. The screen owns the
   * query, so it is told, as it is told to go back to page one.
   */
  onSortParamChange?: (sort: string | undefined) => void;
  /**
   * Boxes on the rows, for acting on a selection of trays: assigning them,
   * archiving or deleting their cases. Section 4 of the request of 24 September
   * 2026, which names the production line. Final Check shares this table and is
   * not named, so it leaves this off.
   */
  selectable?: boolean;
  /**
   * A Received column, the day each case arrived at the lab. Final Check asks
   * for it and a bench does not. The request of 7 October 2026, section 4.
   */
  showReceived?: boolean;
  /** A figure over the table pressed: the table narrowed to what it counts. */
  focus?: StageFocus | null;
}

/** What a selection at a bench can have done to it. */
const STAGE_ACTIONS: readonly SelectionAction[] = ['assign', 'archive', 'delete'];
const kitKey = (kit: Kit) => kit.id;

/**
 * The cases at one stage, with their filter bar.
 *
 * The stage detail and Final Check list the same thing, the trays sitting at one
 * bench, so they share one table rather than two that drift. Filtered in memory:
 * a stage holds tens of trays and the list is already fetched.
 */
export function StageKitTable({
  kits,
  caption,
  emptyHeading,
  emptyDetail,
  onRowClick,
  tableKey,
  layoutKey,
  paging,
  onListChanged,
  onSortParamChange,
  selectable = false,
  showReceived = false,
  focus = null,
}: StageKitTableProps) {
  // Boxes only where the screen asks for them, and only for somebody who may act.
  const permitted = usePermittedActions(STAGE_ACTIONS);
  const actions = selectable ? permitted : [];
  const selection = useSelection(kitKey);
  const { data: session } = useSession();
  // The Assigned cell is a button for a holder of assign, so a manager looking at a
  // bench can hand out the whole bench without opening each case. Section 1.5.
  const canAssign = session?.permissions.includes('assign') === true;
  const [assigning, setAssigning] = useState<Kit | null>(null);
  const [query, setQuery] = useState('');
  const [input, setInput] = useState<InputType | 'all'>('all');
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);
  // The cases sent back for rework, which the Rework figure counts. The lab's request of 17 October 2026.
  const [reworkOnly, setReworkOnly] = useState(false);
  const [assignee, setAssignee] = useState(ANYONE);

  /*
   * Urgent first is the default: it is the order a bench already works in, and
   * the order GET /kits already answers in. Section 3 of the request of
   * 13 September 2026.
   */
  const presets = useMemo(
    () =>
      casePresets<Kit>([
        // Newest first unless the reader chose otherwise. Request of 5 October 2026, section 2.
        'newestFirst',
        'urgentFirst',
        'dueSoonest',
        'longestWaiting',
        'doctorAtoZ',
        // One origin's trays first. Section 2 of the request of 24 September 2026.
        ...ORIGIN_SORT_KEYS,
      ]),
    [],
  );
  const order = useTableOrder(`stage:${tableKey}`, presets);

  /*
   * A figure pressed over the table: every other filter off, and the one the
   * figure counts by on, or the longest waiting first. Read through a ref, so
   * only a press changes the table and not a new render.
   */
  const choose = useRef(order.choose);
  choose.current = order.choose;
  useEffect(() => {
    if (focus === null) return;
    setQuery('');
    setInput('all');
    setUrgentOnly(false);
    setAssignee(ANYONE);
    setOverdueOnly(focus.show === 'overdue');
    setReworkOnly(focus.show === 'rework');
    if (focus.show === 'oldest') choose.current('longestWaiting');
  }, [focus]);
  // The columns as this reader laid them out, kept on their account. The request of 7 October 2026, section 3.
  const layout = useTableLayout(layoutKey ?? tableKey);

  // Including the order a remembered preset starts in, so the first page is already right.
  useEffect(() => {
    onSortParamChange?.(order.sortParam);
  }, [onSortParamChange, order.sortParam]);

  /*
   * A filter or a new order makes a different list, and page three of the old
   * one is nothing in the new one. The screen owns the page, so it is told.
   */
  useEffect(() => {
    onListChanged?.();
  }, [onListChanged, query, input, urgentOnly, overdueOnly, reworkOnly, assignee, order.presetKey, order.column]);

  // The lab's day, which Overdue, Due today and Due tomorrow are measured against.
  const today = labToday();

  /*
   * The people holding a tray here, by name. Only whoever actually has a tray at
   * this stage is offered: a list of the whole team would offer choices that
   * can only ever show nothing. The names come with the kits now, so nothing
   * here has to fetch the team or ever fall back to an id.
   */
  const holders = useMemo(() => {
    const names = new Map<string, string>();
    for (const kit of kits) {
      if (kit.assignedToUserId !== null) {
        names.set(kit.assignedToUserId, kit.assignedToUserName ?? strings.stage.assigned);
      }
    }
    return [...names]
      .map(([id, label]) => ({ value: person(id), label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [kits]);

  const visible = kits.filter(
    (kit) =>
      // The kit id is not a column any more and is still searched: whoever is
      // holding the tray types what is printed on it. Section 8.
      matchesSearch(query, [kit.jobOrderNo, kit.id, kit.dentistName, kit.patientName]) &&
      (input === 'all' || kit.input === input) &&
      (!urgentOnly || kit.urgent) &&
      (!overdueOnly || listsAsOverdue(kit, today)) &&
      (!reworkOnly || kit.rework) &&
      (assignee === ANYONE ||
        (assignee === UNASSIGNED
          ? kit.assignedToUserId === null
          : kit.assignedToUserId !== null && person(kit.assignedToUserId) === assignee)),
  );

  const filtering =
    query.trim() !== '' || input !== 'all' || urgentOnly || overdueOnly || reworkOnly || assignee !== ANYONE;

  const filters: FilterDef[] = [
    {
      kind: 'search',
      label: strings.stage.search,
      placeholder: strings.stage.searchPlaceholder,
      value: query,
      onChange: setQuery,
    },
    {
      kind: 'select',
      label: strings.stage.input,
      value: input,
      options: [
        { value: 'all', label: strings.stage.allInputs },
        { value: 'digital', label: INPUT_TYPE_LABEL.digital },
        { value: 'impression', label: INPUT_TYPE_LABEL.impression },
      ],
      onChange: (next) => setInput(next as InputType | 'all'),
    },
    { kind: 'toggle', label: strings.stage.urgentOnly, value: urgentOnly, onChange: setUrgentOnly },
    { kind: 'toggle', label: strings.stage.overdueOnly, value: overdueOnly, onChange: setOverdueOnly },
    { kind: 'toggle', label: strings.stage.reworkOnly, value: reworkOnly, onChange: setReworkOnly },
    {
      kind: 'select',
      label: strings.stage.assignedTo,
      value: assignee,
      options: [
        { value: ANYONE, label: strings.stage.anyone },
        { value: UNASSIGNED, label: strings.stage.unassigned },
        ...holders,
      ],
      onChange: setAssignee,
    },
  ];

  const columns: Column<Kit>[] = [
    {
      key: 'product',
      label: strings.stage.colItem,
      sortable: true,
      sortValue: (kit) => kit.product,
      render: (kit) =>
        kit.teeth.length === 0 ? kit.product : `${kit.product} · ${kit.teeth.join(', ')}`,
    },
    {
      key: 'input',
      label: strings.stage.colInput,
      render: (kit) => INPUT_TYPE_LABEL[kit.input],
    },
    {
      /*
       * Where the case comes from, so the origin sorts show on the rows they
       * move. Section 2 of the request of 24 September 2026. The header sorts
       * it in the client's order, not the alphabet's.
       */
      key: 'origin',
      label: strings.table.origin,
      sortable: true,
      sortValue: (kit) => ORIGINS.indexOf(kit.origin),
      render: (kit) => ORIGIN_LABEL[kit.origin],
    },
    {
      key: 'assignedTo',
      label: strings.stage.colAssigned,
      sortable: true,
      sortValue: (kit) => kit.assignedToUserName ?? '',
      render: (kit) => {
        const name =
          kit.assignedToUserId === null
            ? strings.stage.unassigned
            : (kit.assignedToUserName ?? strings.stage.assigned);
        if (!canAssign) return name;
        return (
          <Button
            variant="text"
            title={strings.assign.action}
            // The row opens the case; this cell opens the picker, and nothing else.
            onClick={(event) => {
              event.stopPropagation();
              setAssigning(kit);
            }}
            onKeyDown={(event) => event.stopPropagation()}
          >
            {name}
          </Button>
        );
      },
    },
    {
      /*
       * Not a strip: a strip per row would be unreadable. Knowing a case HAS
       * photographs is what makes somebody open it, which is the whole of what
       * section 6 of the request of 16 September 2026 asks a list for.
       */
      key: 'photoCount',
      label: strings.stage.colPhotos,
      align: 'right',
      sortable: true,
      sortValue: (kit) => kit.photoCount,
      render: (kit) => <CasePhotoCount count={kit.photoCount} />,
    },
    ...(showReceived
      ? [
          {
            key: 'receivedAt',
            label: strings.finalCheck.work.received,
            mono: true,
            nowrap: true,
            sortable: true,
            sortValue: (kit: Kit) => kit.receivedAt ?? '',
            render: (kit: Kit) => (kit.receivedAt == null ? '' : formatDay(kit.receivedAt)),
          } satisfies Column<Kit>,
        ]
      : []),
    {
      key: 'minutesInStage',
      label: strings.stage.colInStage,
      mono: true,
      nowrap: true,
      sortable: true,
      sortValue: (kit) => kit.minutesInStage,
      render: (kit) => formatDuration(kit.minutesInStage * 60),
    },
    /*
     * What a tray carries and the list does not show as it comes, offered in
     * Customize columns. The request of 8 October 2026 (second).
     */
    {
      key: 'deliveryDate',
      label: strings.table.columns.due,
      nowrap: true,
      sortable: true,
      optional: true,
      sortValue: (kit) => kit.deliveryDate,
      // In the tone its date reads in everywhere; a tray at Ready is late on nobody's bench.
      render: (kit) => <DeliveryDate date={kit.deliveryDate} finished={kit.stage === 'ready'} />,
    },
    {
      key: 'startDate',
      label: strings.table.columns.startDate,
      mono: true,
      nowrap: true,
      sortable: true,
      optional: true,
      sortValue: (kit) => kit.startDate,
      render: (kit) => kit.startDate,
    },
    // Final Check shows the day a case arrived as it comes; a bench offers it.
    ...(showReceived
      ? []
      : [
          {
            key: 'receivedAt',
            label: strings.table.columns.received,
            mono: true,
            nowrap: true,
            sortable: true,
            optional: true,
            sortValue: (kit: Kit) => kit.receivedAt ?? '',
            render: (kit: Kit) => (kit.receivedAt == null ? '' : formatDay(kit.receivedAt)),
          } satisfies Column<Kit>,
        ]),
    {
      key: 'units',
      label: strings.table.columns.units,
      mono: true,
      align: 'right',
      sortable: true,
      optional: true,
      sortValue: (kit) => kit.units,
      render: (kit) => kit.units,
    },
    {
      key: 'shade',
      label: strings.table.columns.shade,
      sortable: true,
      optional: true,
      sortValue: (kit) => kit.shade,
      render: (kit) => kit.shade,
    },
    {
      key: 'noteCount',
      label: strings.table.columns.notes,
      mono: true,
      align: 'right',
      sortable: true,
      optional: true,
      sortValue: (kit) => kit.noteCount,
      render: (kit) => (kit.noteCount === 0 ? '' : kit.noteCount),
    },
    {
      // What is printed on the tray's sheet, which the search above already finds.
      key: 'kitId',
      label: strings.table.columns.kit,
      mono: true,
      nowrap: true,
      sortable: true,
      optional: true,
      sortValue: (kit) => kit.id,
      render: (kit) => kit.id,
    },
    {
      key: 'assignedBy',
      label: strings.table.columns.assignedBy,
      sortable: true,
      optional: true,
      sortValue: (kit) => kit.assignedByUserName ?? '',
      render: (kit) => kit.assignedByUserName ?? '',
    },
    /*
     * More of what a case carries. The lab's request of 11 October 2026, for
     * Final Check and the production line: how long the lab has had it, whose
     * clinic it is for, what it remakes, what came with it and what was asked.
     */
    {
      // Whole days since it arrived at the lab.
      key: 'inLab',
      label: strings.table.columns.inLab,
      mono: true,
      align: 'right',
      nowrap: true,
      sortable: true,
      optional: true,
      sortValue: (kit) => daysInLab(kit.receivedAt, today) ?? -1,
      render: (kit) => {
        const days = daysInLab(kit.receivedAt, today);
        return days === null ? '' : `${days}${strings.orders.daysShort}`;
      },
    },
    {
      // Identity, as the two names are: Hidden for a reader who is not shown them.
      key: 'clinic',
      label: strings.table.columns.clinic,
      sortable: true,
      optional: true,
      sortValue: (kit) => kit.clinic ?? '',
      render: (kit) => (kit.clinic === undefined ? strings.table.hidden : (kit.clinic ?? '')),
    },
    {
      key: 'redoOf',
      label: strings.table.columns.redoOf,
      mono: true,
      nowrap: true,
      sortable: true,
      optional: true,
      sortValue: (kit) => kit.redoOf ?? '',
      render: (kit) => kit.redoOf ?? '',
    },
    {
      // Impression, Model, Bite: what arrived with the case.
      key: 'cameWith',
      label: strings.table.columns.cameWith,
      optional: true,
      render: (kit) => {
        const items = itemsText(kit.itemsReceived);
        return items === CHANGE_NONE ? '' : items;
      },
    },
    {
      key: 'instructions',
      label: strings.table.columns.instructions,
      optional: true,
      render: (kit) => kit.instructions,
    },
  ];

  return (
    <>
      <FilterBar
        label={strings.stage.filtersLabel}
        filters={filters}
        sort={<SortSelect presets={presets} value={order.presetKey} onChange={order.choose} />}
      />
      <CaseTable
        customize={layout}
        columns={columns}
        rows={order.apply(visible)}
        selection={actions.length > 0 ? selection : undefined}
        sort={order.column}
        onSortChange={order.sortByColumn}
        identity={(kit) => ({
          jobOrderNo: kit.jobOrderNo,
          urgent: kit.urgent,
          redo: kit.redoOf != null,
          held: kit.held,
          dentistName: kit.dentistName,
          patientName: kit.patientName,
        })}
        status={(kit) => {
          const status = kitStatus({ ...kit, finished: kit.stage === 'ready' }, today);
          // A deletion waiting for approval locks nothing at the bench; it is marked.
          return (
            <StatusWithNote
              tone={status.tone}
              note={kit.deletionStatus === 'pending' ? strings.deletion.awaitingApproval : null}
            >
              {status.label}
            </StatusWithNote>
          );
        }}
        statusSortValue={(kit) => kit.daysLeft}
        rowKey={(kit) => kit.id}
        onRowClick={onRowClick}
        caption={caption}
        emptyHeading={filtering ? strings.table.noMatch : emptyHeading}
        emptyDetail={filtering ? strings.table.noMatchDetail : emptyDetail}
        paging={paging}
      />
      {/* After the table, so the bar appearing moves no row under the pointer. */}
      <SelectionActions
        count={selection.count}
        jobOrderNos={[...new Set(selection.rows.map((kit) => kit.jobOrderNo))]}
        assignTarget={{ kitIds: selection.rows.map((kit) => kit.id) }}
        commonStage={commonStageOf(selection.rows.map((kit) => kit.stage))}
        actions={actions}
        onClear={selection.clear}
        onSelectPage={
          visible.every((kit) => selection.selected.has(kit.id))
            ? undefined
            : () => selection.onTogglePage(visible, true)
        }
      />
      {assigning !== null && <AssignDialog kit={assigning} onClose={() => setAssigning(null)} />}
    </>
  );
}

